import { config } from '../config';

// Get the JWT token from localStorage
const getAuthToken = (): string | null => {
  // First check if we have a token in localStorage
  const token = localStorage.getItem('AUTH_TOKEN');
  if (token) return token;

  // If not, check if we have the auth data from the verify_otp response
  const authData = localStorage.getItem('AUTH_DATA');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      return parsed.token;
    } catch {
      return null;
    }
  }

  return null;
};

// Set the JWT token in localStorage
const setAuthToken = (token: string) => {
  localStorage.setItem('AUTH_TOKEN', token);
};

// Set the auth data from verify_otp response
const setAuthData = (data: any) => {
  localStorage.setItem('AUTH_DATA', JSON.stringify(data));
  if (data.token) {
    setAuthToken(data.token);
  }
};

// Clear auth data
const clearAuth = () => {
  localStorage.removeItem('AUTH_TOKEN');
  localStorage.removeItem('AUTH_DATA');
  localStorage.removeItem(config.USER_EMAIL_KEY);
  localStorage.removeItem(config.USER_ROLE_KEY);
};

// Authenticated fetch wrapper
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getAuthToken();

  // If no token, proceed without auth (for public endpoints)
  if (!token) {
    return fetch(url, options);
  }

  // Add Authorization header for protected endpoints
  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(url, {
    ...options,
    headers
  });

  // If token is expired or invalid, clear auth and redirect to login
  if (response.status === 401) {
    clearAuth();
    window.location.href = '/auth';
    throw new Error('Session expired. Please log in again.');
  }

  return response;
};

// API request helper
const apiRequest = async (endpoint: string, method: string = 'GET', body?: any): Promise<any> => {
  const url = `${config.API_BASE_URL}${endpoint}`;
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await authenticatedFetch(url, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
};

export {
  getAuthToken,
  setAuthToken,
  setAuthData,
  clearAuth,
  authenticatedFetch,
  apiRequest
};